/**
 * InstallDemoVideo (Plan 06-03, Task 1).
 *
 * The looping iPhone screen-recording that demonstrates the install
 * flow. Renders the bundled `/install-demo.mp4` (lands in Plan 06-09)
 * with a poster image (`/install-demo-poster.png` — placeholder until
 * 06-09 generates the real first-frame).
 *
 * Pitfall P-05 (iOS Safari <video> quirks): the four attributes that
 * are non-negotiable for iOS inline auto-playback are `autoPlay`,
 * `muted`, `loop`, and `playsInline` — all four are present. iOS
 * Safari requires `muted` for unattended autoplay, and `playsInline`
 * stops the video from launching the fullscreen native player on
 * iPhone (which would interrupt the install walkthrough).
 *
 * WCAG SC 1.2.2 (captions): we render an empty `<track kind="captions"
 * srcLang="nb">`. The video has no audio (it is a silent screen-
 * recording), so an empty captions track is the documented graceful
 * no-op — it structurally satisfies the requirement that prerecorded
 * audio content (here, the absence thereof) be captioned.
 *
 * Fallback: on `<video>` error (network failure, format unsupported,
 * 404), the component re-renders as a `role="alert"` block with the
 * poster image, an `Icon.AlertShield`, and the localized `install.
 * video_fallback` prose ("Videoen kunne ikke spilles av — følg
 * stegene under"). The step list below is then the sole source of
 * truth.
 *
 * i18n keys: install.video_aria, install.video_fallback.
 */
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/primitives';

export interface InstallDemoVideoProps {
  className?: string;
}

export function InstallDemoVideo({ className }: InstallDemoVideoProps = {}): JSX.Element {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div
        role="alert"
        data-testid="install-video-fallback"
        className={[
          'flex flex-col items-center gap-3 rounded-lg bg-raised p-4 text-center',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <img
          src="/install-demo-poster.png"
          alt={t('install.video_fallback')}
          className="w-full max-w-90 rounded-md shadow-sm"
        />
        <div className="flex items-center justify-center gap-2 text-bad">
          <Icon.AlertShield size={20} className="text-bad" />
          <p className="text-sm text-ink-2">{t('install.video_fallback')}</p>
        </div>
      </div>
    );
  }

  return (
    <video
      autoPlay
      muted
      loop
      playsInline
      poster="/install-demo-poster.png"
      aria-label={t('install.video_aria')}
      onError={() => setFailed(true)}
      className={['w-full rounded-lg shadow-md', className].filter(Boolean).join(' ')}
    >
      <source src="/install-demo.mp4" type="video/mp4" />
      <track kind="captions" srcLang="nb" label="Norsk" default />
    </video>
  );
}
